/**
 * Constructor.io — Search web component
 *
 * Renders search results on the /search page via Constructor's search API.
 * Extends ConstructorFilteredPaginatedBase for shared pagination, filter,
 * sort, and rendering logic. Product cards are fetched via ConstructorBase
 * using /collections/all?view=list-products.
 *
 * When the theme snippet includes Constructor facets (filter/sort bar), the
 * base class passes filters/sort into getSearchResults and emits
 * constructor-search:data for constructor-facets.js (same pattern as browse).
 *
 * Attributes (set from Liquid):
 *   products-to-show  — number of results per page
 *   item-class        — space-separated CSS classes added to each <li>
 */
class ConstructorSearch extends ConstructorFilteredPaginatedBase {
  _surfaceName() {
    return 'search';
  }

  _sortRadioIdPrefix() {
    return 'ConstructorSearchSortBy-';
  }

  connectedCallback() {
    this.searchTerm = new URLSearchParams(window.location.search).get('q') || '';
    this.productsToShow = parseInt(this.getAttribute('products-to-show'), 10) || 12;
    this.itemClasses = (this.getAttribute('item-class') || '').trim().split(/\s+/).filter(Boolean);
    this.productsContainer = this.querySelector('.js-container-for-products');
    this.fallbackUrl = this.getAttribute('fallback-url') || '';
    this._noResultsStatus = this.querySelector('.js-constructor-no-results-status');
    this._totalResultsEl = this.querySelector('.js-constructor-total-results');
    this._bestsellersEl = this.querySelector('.js-constructor-bestsellers');
    this._emptyState = this.querySelector('.js-constructor-empty-state');
    this._cnstrcSearchEl = this.querySelector('[data-cnstrc-search]');

    this._initFilterAndSort();
    this._initSearchForm();
    this.classList.add('is-observed');
    this._initPagination();

    if (this.searchTerm) {
      this._load();
    } else {
      this._showEmptyPage();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._inputAbortController?.abort();
  }

  _renderHtml(htmlText) {
    const isReplace = this.currentPage === 1 || this._isInitialRender;
    const gridItemsBefore = isReplace ? 0 : this.productsContainer.querySelectorAll('li.grid__item').length;

    super._renderHtml(htmlText);

    // On load-more, only class newly added items (avoids re-classing the whole grid).
    const gs = document.querySelector('grid-switcher');
    if (gs) {
      const allItems = this.productsContainer.querySelectorAll('li.grid__item');
      if (isReplace) {
        gs.applyGridView?.(allItems);
      } else if (typeof gs.updateGridClasses === 'function') {
        gs.updateGridClasses([...allItems].slice(gridItemsBefore));
      } else {
        gs.applyGridView?.(allItems);
      }
    }
    this._updateTotalResults();
    this._syncCnstrcSearchAttributes();
  }

  _syncCnstrcSearchAttributes() {
    if (!this._cnstrcSearchEl) return;
    const term = this.searchTerm || '';
    const count = this._totalNumResults ?? 0;
    const page = this.currentPage ?? 1;
    this._cnstrcSearchEl.setAttribute('data-cnstrc-search-term', term);
    this._cnstrcSearchEl.setAttribute('data-cnstrc-num-results', String(count));
    this._cnstrcSearchEl.setAttribute('data-cnstrc-result-page', String(page));
  }

  _initSearchForm() {
    const wrapper = this.closest('.template-search');
    const input = wrapper?.querySelector('#Search-In-Template');
    if (!input) return;

    if (this.searchTerm) input.value = this.searchTerm;

    const resetBtn = input.closest('form')?.querySelector('.reset__button');
    if (!resetBtn) return;

    if (this.searchTerm) resetBtn.classList.remove('hidden');

    this._inputAbortController = new AbortController();
    input.addEventListener('input', () => resetBtn.classList.toggle('hidden', input.value === ''), {
      signal: this._inputAbortController.signal,
    });
  }

  async _fetchConstructorResults() {
    if (!window.constructorio?.search?.getSearchResults) {
      throw new Error('constructorio.search.getSearchResults is not available');
    }

    const pag = this._getPaginationParams();
    const opts = this._applyFilterAndSortTo({ page: pag.page, resultsPerPage: pag.resultsPerPage });

    const result = await window.constructorio.search.getSearchResults(this.searchTerm, opts);

    this._totalNumResults = result?.response?.total_num_results ?? 0;

    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const results = result?.response?.results || [];
      publish(PUB_SUB_EVENTS.searchQuery, {
        query: this.searchTerm,
        search_context: 'results_page',
        engine: 'constructor',
        products_count: this._totalNumResults,
        collections_count: 0,
        result_product_ids: results.map(r => r.data?.handle || r.data?.id || r.value).filter(Boolean)
      });
    }

    return result;
  }

  // Prefetch next page without firing search analytics — publish runs on consume via apply().
  async _requestConstructorForPrefetch(nextPage) {
    if (!window.constructorio?.search?.getSearchResults) return null;

    const opts = this._applyFilterAndSortTo({ page: nextPage, resultsPerPage: this.productsToShow });
    const result = await window.constructorio.search.getSearchResults(this.searchTerm, opts);
    const totalNumResults = result?.response?.total_num_results ?? 0;
    const results = result?.response?.results || [];

    return {
      result,
      apply: () => {
        this._totalNumResults = totalNumResults;
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.searchQuery, {
            query: this.searchTerm,
            search_context: 'results_page',
            engine: 'constructor',
            products_count: this._totalNumResults,
            collections_count: 0,
            result_product_ids: results.map((r) => r.data?.handle || r.data?.id || r.value).filter(Boolean),
          });
        }
      },
    };
  }

  _productsFoundText(count) {
    const template =
      count === 1
        ? window.constructorStrings?.productsFoundOne || 'There is __count__ product found'
        : window.constructorStrings?.productsFoundOther || 'There are __count__ products found';
    return ConstructorBase._decodeEntities(template).replace('__count__', count);
  }

  _updateTotalResults() {
    if (!this._totalResultsEl) return;
    this._totalResultsEl.textContent = this._productsFoundText(this._totalNumResults);
    this._totalResultsEl.hidden = false;
  }

  _onNoResults() {
    this._renderedCount = 0;
    if (this.productsContainer) this.productsContainer.innerHTML = '';
    if (this.paginationContainer) this.paginationContainer.innerHTML = '';

    if (this._noResultsStatus) {
      const tpl = window.constructorStrings?.noResultsForTerm || 'No results for "__terms__"';
      this._noResultsStatus.textContent = ConstructorBase._decodeEntities(tpl).replace('__terms__', this.searchTerm);
      this._noResultsStatus.hidden = false;
    }

    if (this._totalResultsEl) {
      this._totalResultsEl.textContent = this._productsFoundText(0);
      this._totalResultsEl.hidden = false;
    }

    if (this._bestsellersEl) this._bestsellersEl.hidden = false;
    this._syncCnstrcSearchAttributes();
  }

  async _onError() {
    console.error('[ConstructorSearch] Failed to load — falling back to Shopify search.');
    if (!(await this._loadNativeSectionFallback())) this._showSearchError();
  }

  _showSearchError() {
    this._renderedCount = 0;
    if (this.productsContainer) this.productsContainer.innerHTML = '';
    if (this.paginationContainer) this.paginationContainer.innerHTML = '';

    if (this._noResultsStatus) {
      const tpl = window.constructorStrings?.searchError || 'Something went wrong. Please try again.';
      this._noResultsStatus.textContent = ConstructorBase._decodeEntities(tpl);
      this._noResultsStatus.hidden = false;
    }
    this._syncCnstrcSearchAttributes();
  }

  _showEmptyPage() {
    this._renderedCount = 0;
    if (this.productsContainer) this.productsContainer.innerHTML = '';
    if (this.paginationContainer) this.paginationContainer.innerHTML = '';
    const wrapper = this.closest('.template-search');
    if (wrapper) wrapper.classList.add('template-search--empty');
    this._syncCnstrcSearchAttributes();
  }
}

customElements.define('constructor-search', ConstructorSearch);
